import { Component, HostListener, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { NgClass, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SidebarService } from '@core/services/side-bar.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPenToSquare, faBarsSort, faRightFromBracket, faGear, faSearch, faAngleDown, faAngleUp, faAngleLeft, faCirclePlus, faSparkles, faSeedling, faTrash, faPaperclip, faUser, faHome, faUsers, faBuildings, faArrowTurnDownLeft, faArrowLeft, faFolder, faFolderOpen, faChevronRight, faChevronDown, faPen, faBriefcase, faRocket, faClipboardList } from '@fortawesome/pro-regular-svg-icons';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import { ChatService } from '@core/services/chat.service';
import { ChatGroupsService, ChatGroupItem } from '@core/services/chat-groups.service';
import { SearchChatViewService } from '@core/services/search-chat-view.service';
import { ChatStateService, BackgroundChatNotification } from '@core/services/chat-state.service';
import { CompanyService } from '@core/services/company.service';
import { AgentStateService, AgentData } from '@core/services/agent-state.service';
import { AuthenticationService } from '@core/services/auth.service';
import { FinancialDocumentsViewService } from '@core/services/financial-documents-view.service';
import { ProfileService } from '@core/services/profile.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NavigationEnd } from '@angular/router';
import { NavbarViewManagerService } from '@core/services/navbar-view-manager.service';
import {
  getAgentCategoryIcon,
  getAgentDescription,
  convertIconToArray,
} from '@core/utils/agent-icon.utils';
import { ChatPreviewFormatUtils } from '@core/utils/chat-preview-format.utils';
import { AlertComponent } from '../alert/alert.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { LoaderComponent } from '../loader/loader.component';
import { NavigationShellStateService } from '@core/services/navigation-shell-state.service';
import { ConnectionsStateService } from '@core/services/connections-state.service';

interface SidebarChatItem {
  chat_id: string;
  id?: number;
  agent_id?: string;
  datasource_id?: string;
  label: string;
  icon: [string, string];
  agent?: any;
  agent_datasource?: any;
  active: boolean;
  created_at?: Date;
  sort_at?: Date;
  isProcessing?: boolean;
  chat_group_id?: number | null;
}

interface SidebarChatGroup {
  id: number;
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  order?: number | null;
  chats_count?: number | null;
}

@Component({
  selector: 'app-sidebar-menu',
  standalone: true,
  imports: [
    NgClass,
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    AlertComponent,
    ConfirmModalComponent,
    LoaderComponent,
  ],
  templateUrl: './sidebar-menu.component.html',
  styleUrl: './sidebar-menu.component.css',
})
export class SidebarMenuComponent implements OnInit, OnDestroy {
  @ViewChild('sidebarScrollContainer') sidebarScrollContainer?: ElementRef<HTMLElement>;
  isOpen = true;
  isMobile = false;
  isMenuExpanded = false;
  darkMode = false;
  showDropdownUp = false;

  private subscriptions: Subscription[] = [];

  agent = {
    id: 0,
    name: '',
    icon: '',
  };
  currentAgentData: any = null;
  agentId: number = Number(localStorage.getItem('agent_id'));

  faPenToSquare = faPenToSquare;
  faBarsSort = faBarsSort;
  faUser = faUser;
  faRightFromBracket = faRightFromBracket;
  faGear = faGear;
  faSearch = faSearch;
  faAngleDown = faAngleDown;
  faAngleUp = faAngleUp;
  faAngleLeft = faAngleLeft;
  faCirclePlus = faCirclePlus;
  faSparkles = faSparkles;
  faSeedling = faSeedling;
  faEllipsis = faEllipsis;
  faTrash = faTrash;
  faPaperclip = faPaperclip;
  faHome = faHome;
  faUsers = faUsers;
  faBuildings = faBuildings;
  faArrowTurnDownLeft = faArrowTurnDownLeft;
  faArrowLeft = faArrowLeft;
  faFolder = faFolder;
  faFolderOpen = faFolderOpen;
  faChevronRight = faChevronRight;
  faChevronDown = faChevronDown;
  faPen = faPen;
  faBriefcase = faBriefcase;
  faRocket = faRocket;
  faClipboardList = faClipboardList;
  
  userInfo = { name: null, company: { name: null } };
  isSmallScreen = false;
  deleteConfirmationOpen: { [key: string]: boolean } = {};
  archiveConfirmationOpen: { [key: string]: boolean } = {};
  chatToDelete: string | null = null;
  chatToArchive: string | null = null;
  isNewChatHovered = false;
  showLogoutConfirmation = false;

  // Toast properties
  show = false;
  toastTitle = '';
  toastMessage = '';
  toastType: 'info' | 'success' | 'warning' | 'alert' = 'success';
  private toastTimer: any = null;
  private lastBackgroundNotification: BackgroundChatNotification | null = null;
  private processingChatIds = new Set<string>();

  /**
   * Fecha de orden asignada localmente (reloj del navegador) a los chats con
   * los que se interactuó en esta sesión.
   *
   * El backend ordena con SU reloj y se han visto desfases de minutos, así que
   * comparar ambas fechas entre sí reordena la lista de forma arbitraria. Los
   * chats de esta sesión se ordenan solo con el reloj local; los históricos,
   * solo con el del servidor.
   */
  private localSortAtByChat = new Map<string, Date>();
  private lastSelectedChatId: string | null = null;

  products: AgentData[] = [];
  filteredProducts: AgentData[] = [];
  agentSearchQuery = '';

  isProfileMenuOpen = false;
  isDropdownAgentOpen = false;
  chatOptionsOpen: { [key: string]: boolean } = {};
  groupOptionsOpen: { [key: number]: boolean } = {};
  ungroupedChats: SidebarChatItem[] = [];
  chatGroupsList: SidebarChatGroup[] = [];
  chatGroupChats: { [key: number]: SidebarChatItem[] } = {};
  chatGroupPagination: {
    [key: number]: { current_page: number; last_page: number; per_page: number; total: number };
  } = {};
  chatGroupExpanded: { [key: number]: boolean } = {};
  chatGroupLoading: { [key: number]: boolean } = {};
  chatGroupStats = {
    total_groups: 0,
    total_chats_in_groups: 0,
    total_chats_without_group: 0,
  };
  defaultGroupIcon = 'folder';
  groupToDelete: SidebarChatGroup | null = null;
  showGroupModal = false;
  isEditingGroup = false;
  groupForm = { name: '', description: '' };
  groupBeingEdited: SidebarChatGroup | null = null;
  isSavingGroup = false;
  showMoveChatModal = false;
  moveChatTargetGroupId: number | null = null;
  moveChatIds: number[] = [];
  moveChatUuids: string[] = [];
  selectionMode = false;
  selectedChatIds = new Set<number>();
  movingChatIds = new Set<string>();
  draggingChat: SidebarChatItem | null = null;
  draggingChatGroupId: number | null = null;
  dragOverGroupId: number | null = null;
  draggingGroupId: number | null = null;

  paginationData = {
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
    from: 0,
    to: 0,
  };

  isLoadingChats = false;
  hasMorePages = false;
  showLoadMoreUngrouped = false;

  constructor(
    private sidebarService: SidebarService,
    private chatService: ChatService,
    private chatGroupsService: ChatGroupsService,
    private searchChatViewService: SearchChatViewService,
    private chatState: ChatStateService,
    private companyService: CompanyService,
    private authService: AuthenticationService,
    private router: Router,
    private financialDocumentsViewService: FinancialDocumentsViewService,
    private navbarViewManagerService: NavbarViewManagerService,
    private agentState: AgentStateService,
    private profileService: ProfileService,
    private navigationShellState: NavigationShellStateService,
    private connectionsStateService: ConnectionsStateService,
    private cdr: ChangeDetectorRef
  ) {
    this.subscriptions.push(
      this.navigationShellState.sidebarMenuState$.subscribe((state) => {
        this.isOpen = state.isSidebarOpen;
        this.isMobile = state.isMobile;
        this.isMenuExpanded = state.isMenuExpanded;
        this.isSmallScreen = state.isSmallScreen;
        if (typeof document !== 'undefined') {
          document.body.classList.toggle(
            'dashboard-menu-collapsed',
            !state.isMenuExpanded,
          );
        }
      }),
    );
  }

  toggleSidebar(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.sidebarService.toggleSidebar();
  }
  /** En ruta Knowledge no cargamos chats ni productos; solo el componente knowledge necesita company. */
  private isKnowledgeRoute(): boolean {
    return this.router.url.includes('/knowledge');
  }

  ngOnInit(): void {
    const currentChatId = this.chatState.getPersistedChatId();
    if (currentChatId && !currentChatId.startsWith('temp_')) {
      this.chatState.setChatId(currentChatId);
    }

    if (!this.isKnowledgeRoute()) {
      if (this.agentState.currentAgents.length > 0) {
        this.products = [...this.agentState.currentAgents];
        this.filteredProducts = [...this.products];
        this.initializeAgent();
      } else {
        this.loadAgentsFromApi();
      }
      this.refreshSidebarData();
    } else {
      // Al salir de /knowledge cargar agentes y chats y suscribirse a refreshChats$
      let refreshChatsSubAdded = false;
      this.subscriptions.push(
        this.router.events.pipe(filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd)).subscribe(() => {
          if (!this.isKnowledgeRoute()) {
            if (this.agentState.currentAgents.length > 0) {
              this.products = [...this.agentState.currentAgents];
              this.filteredProducts = [...this.products];
              this.initializeAgent();
            } else {
              this.loadAgentsFromApi();
            }
            this.refreshSidebarData();
            if (!refreshChatsSubAdded) {
              refreshChatsSubAdded = true;
              this.subscriptions.push(
                this.chatState.refreshChats$.subscribe(() => this.refreshSidebarData())
              );
            }
          }
        })
      );
    }

    this.subscriptions.push(
      this.agentState.agents$.subscribe((data: AgentData[]) => {
        this.products = [...data];
        this.filteredProducts = [...this.products];
        this.filterAgents(); // Aplicar filtro actual si existe
        if (data.length > 0) {
          this.initializeAgent();
        }
      })
    );

    // Suscribirse a cambios del agente actual desde chatState
    this.subscriptions.push(
      this.chatState.currentAgent$.subscribe((agentName) => {
        if (agentName && agentName !== this.agent.name) {
          this.agent.name = agentName;
        }
      })
    );

    this.subscriptions.push(
      this.agentState.refreshAgents$.subscribe(() => {
        this.forceLoadAgentsFromApi();
      })
    );

    if (!this.isKnowledgeRoute()) {
      this.chatState.refreshChats$.subscribe(() => {
        this.refreshSidebarData();
      });
    }

    this.subscriptions.push(
      this.chatState.selectedChatId$.subscribe((chatId) => {
        this.lastSelectedChatId = chatId;
        this.applyActiveChat(chatId);
      })
    );

    // Suscripción para deseleccionar todos los chats cuando se inicia un nuevo chat
    this.subscriptions.push(
      this.chatState.deselectAllChats$.subscribe(() => {
        this.ungroupedChats.forEach((i) => (i.active = false));
        Object.keys(this.chatGroupChats).forEach((groupId) => {
          this.chatGroupChats[Number(groupId)]?.forEach((item) => {
            item.active = false;
          });
        });
      })
    );

    // Crear un chat local en la barra lateral en cuanto se envía el primer
    // mensaje, antes de que el backend devuelva el chat definitivo. Cuando
    // llegue la respuesta de la API y se dispare refreshChats$, la lista se
    // reconstruirá con los datos reales (incluido el chat_id definitivo).
    this.subscriptions.push(
      this.chatState.messageSent$.subscribe((sent) => {
        if (!sent) {
          return;
        }

        // Intentar obtener el chat actual desde el estado o desde localStorage
        let currentChatId =
          this.chatState.getSelectedChatId() ||
          this.chatState.getPersistedChatId();

        // Si ya existe un chat real en el menú con este id, no crear otra fila;
        // solo subirlo al tope, que es lo que corresponde al recibir un
        // mensaje nuevo en una conversación ya existente.
        if (currentChatId) {
          const exists = this.findChatByUuid(currentChatId);
          if (exists && !currentChatId.startsWith('temp_')) {
            this.consumeChatGroupForChatId(currentChatId);
            this.consumePendingChatGroupId();
            this.bumpChatRowToTop(currentChatId);
            return;
          }
        }

        // Si aún no hay chat_id, creamos uno temporal compartido con el chat-box
        if (!currentChatId) {
          currentChatId = this.chatState.ensurePersistedChatId();
          this.chatState.setChatId(currentChatId);
        }

        // Evitar duplicados para este chat (p. ej. siguientes mensajes mientras
        // siga siendo temp_). La comparación resuelve temp↔real: si la fila ya
        // existe bajo el id temporal y ahora llega el real (o al revés), es el
        // MISMO chat y no debe crearse una segunda fila.
        if (this.hasChatRowFor(currentChatId)) {
          this.bumpChatRowToTop(currentChatId);
          return;
        }

        // El label debe salir de los mensajes de ESTE chat, no de
        // `currentMessages` (que son los del chat que se está VIENDO). Con dos
        // chats en vuelo, esto creaba la fila de un chat con la pregunta del
        // otro.
        const messages = this.getMessagesForChatRow(currentChatId);
        const firstUserMessage = messages.find(
          (m: any) => m && m.sender === 'user' && m.text,
        );
        const label = (firstUserMessage?.text || '').trim();
        if (!label) {
          return;
        }

        const typeAgent = localStorage.getItem('type_agent');
        const agentIdStr = localStorage.getItem('agent_id');
        const isDatasource = typeAgent === 'datasource';

        let numericAgentId: number | null = null;
        if (agentIdStr) {
          const parsed = Number(agentIdStr);
          numericAgentId = isNaN(parsed) ? null : parsed;
        }

        let agentObj: any = null;
        if (numericAgentId !== null) {
          agentObj = this.agentState.findAgentById(
            numericAgentId,
            isDatasource,
          );
        }

        const categoryIcon = agentObj
          ? getAgentCategoryIcon(agentObj)
          : getAgentCategoryIcon({});
        const iconArray = convertIconToArray(categoryIcon) as [string, string];

        const now = new Date();
        // Fecha de orden local: por encima de cualquier fecha ya conocida
        // (aunque el servidor vaya adelantado) y registrada para que un
        // refresh posterior no la pise con la del backend.
        const localSortAt = this.nextTopSortDate();
        this.rememberLocalSortAt(currentChatId, localSortAt);
        const newItem: SidebarChatItem = {
          chat_id: currentChatId,
          agent_id: !isDatasource
            ? agentObj?.agent_id ??
              agentObj?.agent_config?.id ??
              (numericAgentId !== null ? numericAgentId.toString() : '')
            : '',
          datasource_id: isDatasource
            ? agentObj?.datasource_id ??
              (numericAgentId !== null ? numericAgentId.toString() : '')
            : undefined,
          label,
          icon: iconArray,
          agent: agentObj || null,
          agent_datasource: isDatasource ? agentObj : null,
          active: true,
          created_at: now,
          sort_at: localSortAt,
          isProcessing: this.processingChatIds.has(currentChatId),
        };

        this.clearActiveChats();
        const mappedGroupId = this.consumeChatGroupForChatId(currentChatId);
        const pendingGroupId = mappedGroupId ?? this.consumePendingChatGroupId();
        if (pendingGroupId) {
          this.ensureGroupChatList(pendingGroupId);
          this.chatGroupChats[pendingGroupId].unshift(newItem);
          this.sortChatList(this.chatGroupChats[pendingGroupId]);
        } else {
          this.ungroupedChats.unshift(newItem);
          this.sortChatList(this.ungroupedChats);
        }
        this.cdr.detectChanges();
      }),
    );

    this.subscriptions.push(
      this.chatState.backgroundResponse$.subscribe((notification) => {
        if (!notification?.chatId) {
          return;
        }
        this.lastBackgroundNotification = notification;
        this.bumpChatSorting(notification.chatId);
        const title = 'Respuesta lista';
        const agent = notification.agentName
          ? ` (${notification.agentName})`
          : '';
        const preview = notification.preview
          ? ` ${notification.preview}`
          : ' Tu chat ya tiene respuesta.';
        this.showToast('info', title, `${preview}${agent}`, 3500, true);
      }),
    );

    this.subscriptions.push(
      this.chatState.activeStreams$.subscribe((streams) => {
        const next = new Set<string>();
        streams.forEach((state) => {
          if (!state.isComplete) {
            const ids = this.chatState.getStreamChatIds(state);
            ids.forEach((id) => next.add(id));
          }
        });
        this.processingChatIds = next;
        // Si el chat ya resolvió su id temporal a uno real (hay mapeo en
        // chatState) y AMBAS filas siguen en la lista, colapsar la temporal
        // ahora mismo. Antes se esperaba al siguiente `loadChats()` para
        // deduplicar, y mientras tanto las dos filas del MISMO chat subían y
        // bajaban juntas en cada cambio de isProcessing — se veía como un
        // reordenamiento constante mientras respondía.
        this.dedupeResolvedChats(this.ungroupedChats);
        Object.keys(this.chatGroupChats).forEach((groupId) => {
          this.dedupeResolvedChats(this.chatGroupChats[Number(groupId)] || []);
        });
        // Reordenar ante CUALQUIER cambio de isProcessing, no solo cuando
        // algo empieza a procesar. Antes, si un chat dejaba de procesar
        // (`willBeProcessing` pasa a false) el array no se volvía a
        // ordenar, y se quedaba mostrado por encima de chats que sí seguían
        // procesando — violando el propio criterio de orden hasta que otra
        // promoción o el siguiente `loadChats()` lo corrigieran por
        // casualidad.
        // Aquí NO se toca `sort_at`. La posición ya la fijó el envío
        // (`messageSent$`), que es el único momento que refleja el orden real
        // en que el usuario mandó los mensajes. Cuando esto también bumpeaba,
        // los chats se reordenaban entre sí según el orden en que se
        // registraban sus streams — un baile de menos de un segundo entre los
        // primeros de la lista.
        let didChange = false;
        this.ungroupedChats.forEach((item) => {
          const willBeProcessing = this.processingChatIds.has(item.chat_id);
          if (willBeProcessing !== !!item.isProcessing) {
            didChange = true;
          }
          item.isProcessing = willBeProcessing;
        });
        Object.keys(this.chatGroupChats).forEach((groupId) => {
          this.chatGroupChats[Number(groupId)]?.forEach((item) => {
            const willBeProcessing = this.processingChatIds.has(item.chat_id);
            if (willBeProcessing !== !!item.isProcessing) {
              didChange = true;
            }
            item.isProcessing = willBeProcessing;
          });
        });
        if (didChange) {
          this.sortAllChatLists();
        }
        this.cdr.markForCheck();
      }),
    );
  }

  /**
   * Sube al tope la fila de un chat existente que acaba de recibir un mensaje
   * nuevo. Es el ÚNICO punto que reposiciona por actividad local, para que el
   * orden entre chats recién enviados lo decida el orden de envío y nada más.
   */
  private bumpChatRowToTop(chatId: string): void {
    const resolvedTarget = this.chatState.resolveChatId(chatId);
    const matches = (item: SidebarChatItem) =>
      item.chat_id === chatId ||
      this.chatState.resolveChatId(item.chat_id) === resolvedTarget;

    const target =
      this.ungroupedChats.find(matches) ??
      Object.keys(this.chatGroupChats)
        .map((groupId) => (this.chatGroupChats[Number(groupId)] || []).find(matches))
        .find((item) => !!item);

    if (!target) {
      return;
    }
    target.sort_at = this.nextTopSortDate();
    this.rememberLocalSortAt(chatId, target.sort_at);
    this.sortAllChatLists();
    this.cdr.markForCheck();
  }

  /**
   * ¿Ya hay una fila para este chat, comparando temp↔real? `findChatByUuid`
   * compara strings exactos, así que al resolverse `temp_x` a su UUID real
   * daba false y se creaba una segunda fila para el mismo chat.
   */
  private hasChatRowFor(chatId: string): boolean {
    if (!chatId) {
      return false;
    }
    const resolvedTarget = this.chatState.resolveChatId(chatId);
    const matches = (item: SidebarChatItem) =>
      item.chat_id === chatId ||
      this.chatState.resolveChatId(item.chat_id) === resolvedTarget;

    if (this.ungroupedChats.some(matches)) {
      return true;
    }
    return Object.keys(this.chatGroupChats).some((groupId) =>
      (this.chatGroupChats[Number(groupId)] || []).some(matches),
    );
  }

  /**
   * Mensajes de un chat concreto para derivar su título en el sidebar.
   * Prioriza la caché por chat (`getMessagesForChat`) sobre el puntero global
   * `currentMessages`, que apunta al chat que se está viendo y no
   * necesariamente al que se está creando.
   */
  private getMessagesForChatRow(chatId: string): any[] {
    const direct = this.chatState.getMessagesForChat(chatId) || [];
    if (direct.length > 0) {
      return direct;
    }
    const resolved = this.chatState.resolveChatId(chatId);
    if (resolved && resolved !== chatId) {
      const byResolved = this.chatState.getMessagesForChat(resolved) || [];
      if (byResolved.length > 0) {
        return byResolved;
      }
    }
    return this.chatState.currentMessages || [];
  }

  /**
   * Quita de `list` cualquier fila con id temporal cuya versión resuelta
   * (real) ya esté presente en la misma lista — evita que un mismo chat se
   * muestre como dos filas independientes mientras siguen "vivas" ambas
   * referencias en el registro de streams.
   */
  private dedupeResolvedChats(list: SidebarChatItem[]): boolean {
    let removed = false;
    for (let i = list.length - 1; i >= 0; i--) {
      const item = list[i];
      if (!item.chat_id?.startsWith('temp_')) {
        continue;
      }
      const resolved = this.chatState.resolveChatId(item.chat_id);
      if (resolved === item.chat_id) {
        continue;
      }
      const hasResolvedRow = list.some(
        (other, idx) => idx !== i && other.chat_id === resolved,
      );
      if (hasResolvedRow) {
        list.splice(i, 1);
        removed = true;
      }
    }
    return removed;
  }

  getParsedIcon(icon: string, agent?: any): [string, string] {
    try {
      if (!icon) {
        // Si no hay icono, usar la lógica de categorías
        if (agent) {
          const categoryIcon = getAgentCategoryIcon(agent);
          return convertIconToArray(categoryIcon);
        }
        return ['far', 'user'];
      }
      
      const iconParsed = JSON.parse(icon);
      // Si existe un icono parseado correctamente, usarlo
      if (iconParsed.style && iconParsed.name) {
        return [iconParsed.style, iconParsed.name];
      } else {
        // Si el JSON no tiene formato correcto, usar categorización
        if (agent) {
          const categoryIcon = getAgentCategoryIcon(agent);
          return convertIconToArray(categoryIcon);
        }
        return ['far', 'user'];
      }
    } catch (error) {
      // Si hay error parseando, usar la lógica de categorías
      if (agent) {
        const categoryIcon = getAgentCategoryIcon(agent);
        return convertIconToArray(categoryIcon);
      }
      return ['far', 'user'];
    }
  }

  getAgentDescription(agent: any): string {
    return getAgentDescription(agent);
  }

  getAgentCategoryIcon(agent: any): any {
    return getAgentCategoryIcon(agent);
  }

  getCurrentAgentIcon(): any {
    // Prioridad 1: Si tenemos el agente completo almacenado y coincide con el nombre actual, usarlo
    if (this.currentAgentData && this.currentAgentData.agent_config?.name === this.agent.name) {
      return getAgentCategoryIcon(this.currentAgentData);
    }
    
    // Prioridad 2: Buscar por ID en products
    const currentAgentId = this.agent.id;
    const typeAgent = localStorage.getItem('type_agent');
    const isDatasource = typeAgent === 'datasource';

    const findCurrentAgent = () => {
      return this.products.find((agent: AgentData) => {
        if (isDatasource) {
          return agent.datasource_id === currentAgentId;
        } else {
          return (
            agent.agent_id === currentAgentId || 
            agent.agent_config?.id === currentAgentId ||
            agent.id === currentAgentId
          );
        }
      });
    };

    let currentAgent = findCurrentAgent();

    // Verificar si el agente encontrado coincide con el nombre actual
    if (currentAgent && currentAgent.agent_config?.name === this.agent.name) {
      return getAgentCategoryIcon(currentAgent);
    }

    // Prioridad 3: Buscar por nombre en products (en caso de que el ID no coincida)
    const findByName = () => {
      return this.products.find((agent: AgentData) => {
        return agent.agent_config?.name === this.agent.name;
      });
    };

    const agentByName = findByName();
    if (agentByName) {
      return getAgentCategoryIcon(agentByName);
    }

    // Prioridad 4: Buscar en agentState
    if (!currentAgent) {
      const foundAgent = this.agentState.findAgentById(currentAgentId, isDatasource);
      if (foundAgent) {
        currentAgent = foundAgent;
      }
    }

    if (currentAgent) {
      return getAgentCategoryIcon(currentAgent);
    }

    // Fallback: usar solo el nombre actual
    const tempAgent = {
      agent_config: {
        name: this.agent.name,
        description: ''
      }
    };
    return getAgentCategoryIcon(tempAgent);
  }

  getChatIcon(item: any): any {
    // Si el item tiene datos completos del agente, usarlos
    if (item.agent) {
      return getAgentCategoryIcon(item.agent);
    }
    
    // Fallback: buscar el agente por ID si solo tenemos el ID
    if (item.agent_id || item.datasource_id) {
      const isDatasource = !!item.datasource_id;
      const agentId = item.agent_id || item.datasource_id;
      
      // Buscar en products
      const foundAgent = this.products.find(agent => {
        if (isDatasource) {
          return agent.datasource_id === agentId;
        } else {
          return (
            agent.agent_id === agentId || 
            agent.agent_config?.id === agentId ||
            agent.id === agentId
          );
        }
      });
      
      if (foundAgent) {
        return getAgentCategoryIcon(foundAgent);
      }
      
      // Buscar en agentState si no se encuentra en products
      const agentFromState = this.agentState.findAgentById(agentId, isDatasource);
      if (agentFromState) {
        return getAgentCategoryIcon(agentFromState);
      }
    }
    
    // Fallback final: usar el ícono que ya estaba guardado
    return item.icon || ['far', 'comments'];
  }

  getGroupIconDisplay(icon?: string | null): string | null {
    if (!icon) {
      return null;
    }
    const trimmed = icon.trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.length <= 2) {
      return trimmed;
    }
    if (/[^\x00-\x7F]/.test(trimmed)) {
      return trimmed;
    }
    return null;
  }

  getGroupIconFa(icon?: string | null, isExpanded: boolean = false): any {
    const normalized = (icon || '').trim().toLowerCase();
    if (!normalized) {
      return isExpanded ? this.faFolderOpen : this.faFolder;
    }

    const iconMap: Record<string, any> = {
      folder: this.faFolder,
      briefcase: this.faBriefcase,
      rocket: this.faRocket,
      users: this.faUsers,
      'user-group': this.faUsers,
      team: this.faUsers,
      people: this.faUsers,
      clipboard: this.faClipboardList,
      'clipboard-list': this.faClipboardList,
      list: this.faClipboardList,
    };

    const mapped = iconMap[normalized];
    if (mapped) {
      return mapped;
    }

    return isExpanded ? this.faFolderOpen : this.faFolder;
  }

  getAgentIcon(agent: any): any {
    return getAgentCategoryIcon(agent);
  }

  private resolveChatSortDate(chat: any, fallback: Date): Date {
    const candidates = [
      chat?.updated_at,
      chat?.last_message_at,
      chat?.last_response_at,
      chat?.last_activity_at,
      chat?.created_at,
    ];
    for (const value of candidates) {
      if (!value) {
        continue;
      }
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    return fallback;
  }

  private getChatSortTime(chat: { sort_at?: Date; created_at?: Date }): number {
    const date = chat.sort_at || chat.created_at;
    return date ? new Date(date).getTime() : 0;
  }

  /** Registra la fecha de orden local de un chat (ver `localSortAtByChat`). */
  private rememberLocalSortAt(chatId: string, sortAt: Date): void {
    if (!chatId) {
      return;
    }
    this.localSortAtByChat.set(this.chatState.resolveChatId(chatId), sortAt);
  }

  /**
   * Fecha de orden local de un chat, si la tiene. Compara resolviendo
   * temp↔real porque la fecha puede haberse guardado cuando el chat todavía
   * era temporal.
   */
  private getLocalSortAt(chatId: string): Date | null {
    if (!chatId) {
      return null;
    }
    const resolvedTarget = this.chatState.resolveChatId(chatId);
    const direct = this.localSortAtByChat.get(resolvedTarget);
    if (direct) {
      return direct;
    }
    for (const [key, value] of this.localSortAtByChat.entries()) {
      if (this.chatState.resolveChatId(key) === resolvedTarget) {
        return value;
      }
    }
    return null;
  }

  /**
   * Fecha de orden definitiva para un chat que vuelve del servidor.
   *
   * Si el chat tuvo actividad local en esta sesión, manda SIEMPRE la fecha
   * local, aunque la del servidor sea mayor: son relojes distintos y
   * mezclarlos hacía que un chat saltara de posición al terminar (su
   * `updated_at` del backend, adelantado, superaba la fecha local del otro).
   */
  private mergeSortDate(chatId: string, serverSortAt: Date): Date {
    return this.getLocalSortAt(chatId) ?? serverSortAt;
  }

  /**
   * Fecha de orden para un chat que acaba de tener actividad local (se envió
   * un mensaje, empezó a procesar).
   *
   * No sirve usar `Date.now()` a secas: las filas que vienen del backend se
   * ordenan con la hora del SERVIDOR, y si los relojes no coinciden (se han
   * visto desfases de minutos) un chat recién creado queda por debajo de
   * chats viejos. Aquí se garantiza que quede por encima de cualquier fecha
   * ya conocida en la lista, venga del reloj que venga.
   */
  private nextTopSortDate(): Date {
    let maxKnown = 0;
    const scan = (list: SidebarChatItem[]) => {
      list.forEach((item) => {
        const time = this.getChatSortTime(item);
        if (time > maxKnown) {
          maxKnown = time;
        }
      });
    };
    scan(this.ungroupedChats);
    Object.keys(this.chatGroupChats).forEach((groupId) => {
      scan(this.chatGroupChats[Number(groupId)] || []);
    });
    return new Date(Math.max(Date.now(), maxKnown + 1000));
  }

  /**
   * Orden puramente por actividad (más reciente arriba).
   *
   * Antes había un nivel previo que ponía los chats "procesando" por encima
   * del resto. Era redundante — al empezar a procesar ya se hace
   * `item.sort_at = now`, que sube el chat al tope por fecha — y además
   * provocaba que, con dos chats en vuelo, el que seguía pendiente saltara al
   * primer puesto en cuanto el otro terminaba, para volver a bajar al
   * terminar él. El spinner ya comunica el estado; la posición no necesita
   * cambiar por eso.
   */
  private sortChatList(list: SidebarChatItem[]): void {
    list.sort((a, b) => this.getChatSortTime(b) - this.getChatSortTime(a));
  }

  private sortAllChatLists(): void {
    this.sortChatList(this.ungroupedChats);
    Object.keys(this.chatGroupChats).forEach((groupId) => {
      this.sortChatList(this.chatGroupChats[Number(groupId)] || []);
    });
  }

  private updateGroupCount(groupId: number | null, delta: number): void {
    if (groupId === null) {
      if (this.chatGroupStats) {
        this.chatGroupStats.total_chats_without_group = Math.max(
          0,
          (this.chatGroupStats.total_chats_without_group || 0) + delta
        );
        this.chatGroupStats.total_chats_in_groups = Math.max(
          0,
          (this.chatGroupStats.total_chats_in_groups || 0) - delta
        );
      }
      return;
    }
    const index = this.chatGroupsList.findIndex((g) => g.id === groupId);
    if (index !== -1) {
      const current = this.chatGroupsList[index].chats_count ?? 0;
      this.chatGroupsList[index] = {
        ...this.chatGroupsList[index],
        chats_count: Math.max(0, current + delta),
      };
    }
    if (this.chatGroupStats) {
      this.chatGroupStats.total_chats_in_groups = Math.max(
        0,
        (this.chatGroupStats.total_chats_in_groups || 0) + delta
      );
      this.chatGroupStats.total_chats_without_group = Math.max(
        0,
        (this.chatGroupStats.total_chats_without_group || 0) - delta
      );
    }
  }

  private moveChatLocally(
    chat: SidebarChatItem,
    toGroupId: number | null,
    fromGroupId: number | null
  ): void {
    this.removeChatFromListsByUuid(chat.chat_id);
    chat.chat_group_id = toGroupId;
    if (toGroupId === null) {
      this.ungroupedChats.unshift(chat);
      this.sortChatList(this.ungroupedChats);
    } else {
      if (this.chatGroupChats[toGroupId]) {
        this.chatGroupChats[toGroupId].unshift(chat);
        this.sortChatList(this.chatGroupChats[toGroupId]);
      }
    }
    if (fromGroupId !== toGroupId) {
      this.updateGroupCount(fromGroupId, -1);
      this.updateGroupCount(toGroupId, 1);
    }
    this.cdr.markForCheck();
  }

  loadChats(page: number = 1, append: boolean = false) {
    const currentChatId =
      this.chatState.getSelectedChatId() || this.lastSelectedChatId;
    this.isLoadingChats = true;

    // Snapshot de la lista ANTES de tocarla: la necesitamos para no perder los
    // chats que solo existen localmente (con id temporal, recién enviados)
    // cuando el servidor todavía no los incluye en su respuesta.
    const previousUngroupedSnapshot = this.ungroupedChats;

    this.chatService.getChats(page, { withoutGroup: true }).subscribe({
      next: (response) => {
        this.paginationData = {
          current_page: response.current_page,
          last_page: response.last_page,
          per_page: response.per_page,
          total: response.total,
          from: response.from,
          to: response.to,
        };

        this.hasMorePages = response.current_page < response.last_page;

        if (!append || page === 1) {
          this.ungroupedChats = [];
        }
        let foundActiveChat = false;
        const nextUngrouped = append ? [...this.ungroupedChats] : [];

        // Chats que ahora mismo solo existen en local (id temporal + stream
        // activo). El servidor puede haber creado ya su registro y devolverlo
        // aquí con el id real ANTES de que el cliente procese la respuesta del
        // POST y aprenda el mapeo temp→real. En esa ventana no hay forma de
        // correlacionarlos por id, y si insertamos la fila del servidor se ve
        // el mismo chat duplicado hasta que el mapeo llega.
        const pendingLocalLabels = new Set(
          previousUngroupedSnapshot
            .filter(
              (item) =>
                item.chat_id?.startsWith('temp_') &&
                this.chatState.hasActiveStreamForChat(item.chat_id),
            )
            .map((item) => (item.label || '').trim())
            .filter(Boolean),
        );

        response.data.forEach((chat: any) => {
          if (chat.chat_group) {
            return;
          }
          const chatId = (chat.chat_id || chat.chat_uuid || chat.uuid || chat.id)?.toString?.();
          if (!chatId) {
            return;
          }
          const firstLine =
            ChatPreviewFormatUtils.toPreviewText(
              chat.first_user_line?.display_content ||
                chat.first_user_line?.content ||
                chat.first_user_line?.message ||
                chat.title,
              '',
            );
          if (firstLine) {
            // Ventana temp→real: si este chat del servidor no lo habíamos
            // visto nunca y coincide con una fila local pendiente, es esa
            // misma conversación con su id ya asignado. Saltarla evita
            // mostrarla duplicada; en el siguiente refresh (ya con el mapeo
            // aprendido) entra con normalidad.
            const alreadyKnown = previousUngroupedSnapshot.some(
              (item) =>
                item.chat_id === chatId ||
                this.chatState.resolveChatId(item.chat_id) ===
                  this.chatState.resolveChatId(chatId),
            );
            if (!alreadyKnown && pendingLocalLabels.has(firstLine.trim())) {
              return;
            }

            // Resolver temp↔real igual que `applyActiveChat()`: si el chat
            // seleccionado todavía se conoce por su id temporal y el servidor
            // ya devuelve el real, siguen siendo el MISMO chat.
            const isActive = currentChatId
              ? currentChatId === chatId ||
                this.chatState.resolveChatId(currentChatId) ===
                  this.chatState.resolveChatId(chatId)
              : false;
            
            // Usar la nueva lógica de categorización de iconos
            let categoryIcon;
            if (chat.agent) {
              categoryIcon = getAgentCategoryIcon(chat.agent);
            } else if (chat.agent_datasource) {
              categoryIcon = getAgentCategoryIcon(chat.agent_datasource);
            } else {
              categoryIcon = getAgentCategoryIcon({}); // Icono por defecto (faUser)
            }
            
            // Convertir icono a array usando la función de utils
            const iconArray = convertIconToArray(categoryIcon);

            const createdAt = chat.created_at
              ? new Date(chat.created_at)
              : new Date();
            // Si este chat ya tenía una fecha de orden local más reciente
            // (porque se envió/empezó a procesar en esta sesión), conservarla:
            // la del servidor viene de otro reloj y puede ser menor, lo que
            // haría que el chat "bajara" de golpe en la lista.
            const sortAt = this.mergeSortDate(
              chatId,
              this.resolveChatSortDate(chat, createdAt),
            );

            const resolvedIsProcessing = this.processingChatIds.has(chatId);

            nextUngrouped.push({
              chat_id: chatId,
              id: chat.id,
              agent_id: chat.agent_id,
              datasource_id: chat.agent_datasource_id ?? undefined,
              label: firstLine,
              icon: iconArray,
              agent: chat.agent || null,
              agent_datasource: chat.agent_datasource || null,
              active: isActive,
              isProcessing: resolvedIsProcessing,
              created_at: createdAt,
              sort_at: sortAt,
              chat_group_id: null,
            });

            if (isActive) {
              foundActiveChat = true;
            }
          }
        });

        // Chats que solo existen localmente (id temporal, se acaban de
        // enviar) y que el servidor todavía no devuelve en esta página: si no
        // los conservamos, `nextUngrouped` los deja fuera y desaparecen del
        // sidebar hasta que consigan su id real — justo lo que se veía como
        // "el loader se apaga" en el chat que seguía pendiente cuando el
        // OTRO chat terminaba y disparaba este refresh.
        const stillPendingLocalOnly = (!append || page === 1)
          ? previousUngroupedSnapshot.filter((item) => {
              if (!item.chat_id?.startsWith('temp_')) {
                return false;
              }
              const resolved = this.chatState.resolveChatId(item.chat_id);
              const alreadyInFreshList = nextUngrouped.some(
                (fresh) =>
                  fresh.chat_id === item.chat_id || fresh.chat_id === resolved,
              );
              if (alreadyInFreshList) {
                return false;
              }
              return this.chatState.hasActiveStreamForChat(item.chat_id);
            })
          : [];

        this.ungroupedChats = [...stillPendingLocalOnly, ...nextUngrouped];
        this.sortChatList(this.ungroupedChats);

        this.userInfo = JSON.parse(localStorage.getItem('user') || '{}');

        this.applyActiveChat(currentChatId || null);

        this.isLoadingChats = false;
        setTimeout(() => this.updateLoadMoreVisibility());
      },
      error: (error) => {
        console.error('Error al cargar los chats:', error);
        this.isLoadingChats = false;
      },
      complete: () => {},
    });
  }

  private refreshSidebarData(): void {
    this.loadChatGroups();
    this.loadChats(1, false);
  }

  private loadChatGroups(): void {
    this.chatGroupsService.getGroups().subscribe({
      next: (response) => {
        const groups = (response?.data || []) as ChatGroupItem[];
        const mapped: SidebarChatGroup[] = groups.map((group) => ({
          id: group.id,
          name: group.name,
          description: group.description ?? null,
          icon: this.normalizeGroupIcon(group.icon),
          color: group.color ?? null,
          order: group.order ?? null,
          chats_count: group.chats_count ?? null,
        }));
        mapped.sort((a, b) => {
          const orderA = a.order ?? 0;
          const orderB = b.order ?? 0;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return a.name.localeCompare(b.name);
        });

        const expanded: { [key: number]: boolean } = {};
        mapped.forEach((group) => {
          if (this.chatGroupExpanded[group.id]) {
            expanded[group.id] = true;
          }
        });

        this.chatGroupsList = mapped;
        this.chatGroupExpanded = expanded;
        this.reloadExpandedGroups();
        this.loadChatGroupStats();
        setTimeout(() => this.updateLoadMoreVisibility());
      },
      error: (error) => {
        console.error('Error al cargar las carpetas:', error);
      },
    });
  }

  private loadChatGroupStats(): void {
    this.chatGroupsService.getStats().subscribe({
      next: (response) => {
        const data = response?.data || response || {};
        this.chatGroupStats = {
          total_groups: data.total_groups ?? 0,
          total_chats_in_groups: data.total_chats_in_groups ?? 0,
          total_chats_without_group: data.total_chats_without_group ?? 0,
        };

        // Actualizar chats_count de cada carpeta con los datos precisos de stats
        const statsGroups: { id: number; chats_count: number }[] = data.groups || [];
        statsGroups.forEach((sg) => {
          const idx = this.chatGroupsList.findIndex((g) => g.id === sg.id);
          if (idx !== -1) {
            this.chatGroupsList[idx] = {
              ...this.chatGroupsList[idx],
              chats_count: sg.chats_count,
            };
          }
        });
      },
      error: () => {
        this.chatGroupStats = {
          total_groups: 0,
          total_chats_in_groups: 0,
          total_chats_without_group: 0,
        };
      },
    });
  }

  private reloadExpandedGroups(): void {
    this.chatGroupsList.forEach((group) => {
      if (this.chatGroupExpanded[group.id]) {
        this.loadGroupChats(group.id);
      }
    });
  }

  private loadGroupChats(groupId: number, page: number = 1, append: boolean = false): void {
    this.chatGroupLoading[groupId] = true;
    this.chatGroupsService.getGroup(groupId, { page, archived: false, deleted: false }).subscribe({
      next: (response) => {
        const data = response?.data || response;
        const chats = response?.chats?.data || data?.chats || [];
        const mapped = chats
          .map((chat: any) => {
            const chatId = (chat.chat_id || chat.chat_uuid || chat.uuid || chat.id)?.toString?.();
            if (!chatId) {
              return null;
            }
            const firstLine =
              ChatPreviewFormatUtils.toPreviewText(
                chat.first_user_line?.display_content ||
                  chat.first_user_line?.content ||
                  chat.first_user_line?.message ||
                  chat.title,
              );

            let categoryIcon;
            if (chat.agent) {
              categoryIcon = getAgentCategoryIcon(chat.agent);
            } else if (chat.agent_datasource) {
              categoryIcon = getAgentCategoryIcon(chat.agent_datasource);
            } else {
              categoryIcon = getAgentCategoryIcon({});
            }
            const iconArray = convertIconToArray(categoryIcon);

            const createdAt = chat.created_at
              ? new Date(chat.created_at)
              : new Date();
            const sortAt = this.resolveChatSortDate(chat, createdAt);

            return {
              chat_id: chatId,
              id: chat.id,
              agent_id: chat.agent_id ?? (chat.agent?.id ? String(chat.agent.id) : ''),
              datasource_id: chat.agent_datasource_id ?? undefined,
              label: firstLine,
              icon: iconArray,
              agent: chat.agent || null,
              agent_datasource: chat.agent_datasource || null,
              active: this.chatState.getSelectedChatId() === chatId,
              isProcessing: this.processingChatIds.has(chatId),
              created_at: createdAt,
              sort_at: sortAt,
              chat_group_id: groupId,
            } as SidebarChatItem;
          })
          .filter((chat: SidebarChatItem | null): chat is SidebarChatItem => Boolean(chat));

        if (append) {
          this.chatGroupChats[groupId] = [
            ...(this.chatGroupChats[groupId] || []),
            ...mapped,
          ];
        } else {
          this.chatGroupChats[groupId] = mapped;
        }

        this.chatGroupPagination[groupId] = {
          current_page: response?.chats?.current_page ?? page,
          last_page: response?.chats?.last_page ?? page,
          per_page: response?.chats?.per_page ?? mapped.length,
          total: response?.chats?.total ?? mapped.length,
        };

        this.sortChatList(this.chatGroupChats[groupId]);

        const groupIndex = this.chatGroupsList.findIndex(
          (group) => group.id === groupId
        );
        if (groupIndex !== -1) {
          this.chatGroupsList[groupIndex] = {
            ...this.chatGroupsList[groupIndex],
            name: data?.name || this.chatGroupsList[groupIndex].name,
            description:
              data?.description ?? this.chatGroupsList[groupIndex].description,
            icon: data?.icon ?? this.chatGroupsList[groupIndex].icon,
            color: data?.color ?? this.chatGroupsList[groupIndex].color,
          };
        }
        this.chatGroupLoading[groupId] = false;
        setTimeout(() => this.updateLoadMoreVisibility());
      },
      error: (error) => {
        console.error('Error al cargar los chats del grupo:', error);
        this.chatGroupLoading[groupId] = false;
      },
    });
  }

  loadMoreGroupChats(groupId: number): void {
    const pagination = this.chatGroupPagination[groupId];
    if (!pagination) {
      this.loadGroupChats(groupId, 1, false);
      return;
    }
    if (this.chatGroupLoading[groupId]) {
      return;
    }
    if (pagination.current_page < pagination.last_page) {
      this.loadGroupChats(groupId, pagination.current_page + 1, true);
    }
  }

  private loadAgentsFromApi(): void {
    if (this.agentState.isAgentsLoaded()) {
      return;
    }

    this.companyService.getCompanyProducts().subscribe({
      next: (data: AgentData[]) => {
        this.agentState.setAgents(data);
        this.agentState.markAgentsAsLoaded();
      },
      error: (error: any) => {
        console.error('Error cargando agentes:', error);
      },
    });
  }

  private forceLoadAgentsFromApi(): void {
    this.companyService.getCompanyProducts().subscribe({
      next: (data: AgentData[]) => {
        this.agentState.setAgents(data);
        this.agentState.markAgentsAsLoaded();
      },
      error: (error: any) => {
        console.error('Error cargando agentes:', error);
      },
    });
  }

  loadMoreChats() {
    if (this.hasMorePages && !this.isLoadingChats) {
      this.loadChats(this.paginationData.current_page + 1, true);
    }
  }
  refreshChats() {
    this.refreshSidebarData();
  }

  onScroll(event: Event) {
    const target = event.target as HTMLElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;

    if (
      scrollPosition >= scrollHeight - 100 &&
      this.hasMorePages &&
      !this.isLoadingChats
    ) {
      this.loadMoreChats();
    }
  }

  private updateLoadMoreVisibility(): void {
    if (this.isLoadingChats || !this.hasMorePages) {
      this.showLoadMoreUngrouped = false;
      return;
    }
    const container = this.sidebarScrollContainer?.nativeElement;
    if (!container) {
      this.showLoadMoreUngrouped = false;
      return;
    }
    const isScrollable = container.scrollHeight > container.clientHeight + 8;
    this.showLoadMoreUngrouped = !isScrollable;
  }
  private configureAgent(agentId: number): void {
    const typeAgent = localStorage.getItem('type_agent');
    const isDatasource = typeAgent === 'datasource';

    const findAgentInProducts = () => {
      return this.products.find((agent: AgentData) => {
        if (isDatasource) {
          return agent.datasource_id === agentId;
        } else {
          return (
            agent.agent_id === agentId || 
            agent.agent_config?.id === agentId ||
            agent.id === agentId
          );
        }
      });
    };

    let foundProduct = findAgentInProducts();

    if (foundProduct) {
      this.currentAgentData = foundProduct; // Actualizar currentAgentData para selección de chat
      this.applyAgentConfiguration(foundProduct, agentId);
    } else {
      const foundAgent = this.agentState.findAgentById(agentId, isDatasource);
      if (foundAgent) {
        this.currentAgentData = foundAgent; // Actualizar currentAgentData para selección de chat
        this.applyAgentConfiguration(foundAgent, agentId);
      } else {
        console.error('Product or agent_config not found for agent:', agentId);
      }
    }
  }

  private applyAgentConfiguration(
    foundProduct: AgentData,
    agentId: number
  ): void {
    if (!foundProduct) {
      console.error('foundProduct is null or undefined for agent:', agentId);
      return;
    }
    
    if (!foundProduct.agent_config) {
      console.error('agent_config is null or undefined for agent:', agentId);
      return;
    }

    const config = {
      name: foundProduct.agent_config.name || `Agente ${agentId}`,
    };

    // Siempre usar la lógica de categorización para determinar el ícono
    // basándose en el nombre y descripción del agente
    const categoryIcon = getAgentCategoryIcon(foundProduct);
    const iconArray = convertIconToArray(categoryIcon);
    const agentIcon = JSON.stringify({ style: iconArray[0], name: iconArray[1] });

    this.agent = {
      id: agentId,
      name: config.name,
      icon: agentIcon,
    };

    this.chatState.setCurrentAgent(this.agent.name.toString());

    localStorage.setItem('agent_id', agentId.toString());
    
    // Forzar detección de cambios para actualizar el ícono del dropdown
    this.cdr.detectChanges();
  }

  setAgent(idAgent: number): void {
    this.configureAgent(idAgent);

    if (idAgent === 3) {
      this.financialDocumentsViewService.showFinancialDocuments();
    } else {
      this.financialDocumentsViewService.hideFinancialDocuments();
    }
  }

  logout() {
    this.showLogoutConfirmation = true;
  }

  private performLogout() {
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/']);
      },
      error: (error) => {
        console.error('Error al cerrar sesión:', error);
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('user');
        this.chatState.setPersistedChatId(null);
        localStorage.removeItem('agent_id');
        localStorage.removeItem('type_agent');
        this.router.navigate(['/']);
      },
    });
  }

  confirmLogout() {
    this.showLogoutConfirmation = false;
    this.performLogout();
  }

  cancelLogout() {
    this.showLogoutConfirmation = false;
  }

  selectItem(item: any) {
    // Limpiar currentAgentData para forzar recálculo del ícono
    this.currentAgentData = null;
    
    const currentChatId = this.chatState.getPersistedChatId();

    let agentIdToSet: string | null = null;

    if (item.datasource_id !== null && item.datasource_id !== undefined) {
      localStorage.setItem('type_agent', 'datasource');
      localStorage.setItem('agent_id', item.datasource_id);
      agentIdToSet = item.datasource_id;
    } else {
      localStorage.setItem('type_agent', 'agent');
      localStorage.setItem('agent_id', item.agent_id);
      agentIdToSet = item.agent_id;
    }

    if (currentChatId !== item.chat_id) {
      this.navbarViewManagerService.closeAllNavbarViews();

      this.chatState.clearMessages();
      if (agentIdToSet) {
        this.setAgent(Number(agentIdToSet));
      }
      this.clearActiveChats();
      item.active = true;
      this.chatState.setPersistedChatId(item.chat_id);
      this.chatState.setChatId(item.chat_id, true);
    } else {
      this.navbarViewManagerService.closeAllNavbarViews();
      this.chatState.setChatId(item.chat_id, true);
    }

    this.router.navigate(['/dashboard/chat']);

    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }
  private closeAllDropdowns() {
    this.isProfileMenuOpen = false;
    this.isDropdownAgentOpen = false;
    this.closeAllChatOptions();
    this.closeAllGroupOptions();
    this.closeAllDeleteConfirmations();
  }

  toggleProfileMenu(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const wasOpen = this.isProfileMenuOpen;
    this.closeAllDropdowns();
    this.isProfileMenuOpen = !wasOpen;
  }
  toggleChatOptions(chatId: string, event: Event) {
    event.stopPropagation();
    const isOpening = !this.chatOptionsOpen[chatId];
    if (isOpening) {
      this.closeAllDropdowns();

      const button = event.currentTarget as HTMLElement;
      const buttonRect = button.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - buttonRect.bottom;

      this.showDropdownUp = spaceBelow < 200;
    }
    this.chatOptionsOpen[chatId] = isOpening;
  }

  toggleGroupExpanded(group: SidebarChatGroup, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const isExpanded = !!this.chatGroupExpanded[group.id];
    this.chatGroupExpanded[group.id] = !isExpanded;
    if (!isExpanded) {
      this.loadGroupChats(group.id);
    }
    setTimeout(() => this.updateLoadMoreVisibility());
  }

  openGroupView(group: SidebarChatGroup, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.closeAllDropdowns();
    this.router.navigate(['/dashboard/chat-group', group.id]);

    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  toggleGroupOptions(groupId: number, event: Event): void {
    event.stopPropagation();
    const isOpening = !this.groupOptionsOpen[groupId];
    if (isOpening) {
      this.closeAllDropdowns();
    }
    this.groupOptionsOpen[groupId] = isOpening;
  }

  closeAllGroupOptions(): void {
    Object.keys(this.groupOptionsOpen).forEach((groupId) => {
      this.groupOptionsOpen[Number(groupId)] = false;
    });
  }

  openCreateGroupModal(): void {
    this.closeAllDropdowns();
    this.isEditingGroup = false;
    this.groupBeingEdited = null;
    this.groupForm = { name: '', description: '' };
    this.showGroupModal = true;
  }

  openEditGroupModal(group: SidebarChatGroup, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.closeAllDropdowns();
    this.isEditingGroup = true;
    this.groupBeingEdited = group;
    this.groupForm = {
      name: group.name,
      description: group.description || '',
    };
    this.showGroupModal = true;
  }

  closeGroupModal(): void {
    this.showGroupModal = false;
    this.isSavingGroup = false;
  }

  saveGroup(): void {
    const name = (this.groupForm.name || '').trim();
    if (!name) {
      return;
    }
    this.isSavingGroup = true;
    if (this.isEditingGroup && this.groupBeingEdited) {
      this.chatGroupsService
        .updateGroup(this.groupBeingEdited.id, {
          name,
          description: (this.groupForm.description || '').trim() || null,
          icon: this.normalizeGroupIcon(this.groupBeingEdited.icon),
        })
        .subscribe({
          next: () => {
            this.closeGroupModal();
            this.loadChatGroups();
          },
          error: (error) => {
            console.error('Error al actualizar la carpeta:', error);
            this.isSavingGroup = false;
          },
        });
      return;
    }

    this.chatGroupsService
      .createGroup({
        name,
        description: (this.groupForm.description || '').trim() || null,
        icon: this.defaultGroupIcon,
      })
      .subscribe({
        next: () => {
          this.closeGroupModal();
          this.loadChatGroups();
        },
        error: (error) => {
          console.error('Error al crear la carpeta:', error);
          this.isSavingGroup = false;
        },
      });
  }

  requestDeleteGroup(group: SidebarChatGroup, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.closeAllDropdowns();
    this.groupToDelete = group;
  }

  confirmDeleteGroup(): void {
    const group = this.groupToDelete;
    if (!group) {
      return;
    }
    this.groupToDelete = null;
    this.chatGroupsService.deleteGroup(group.id).subscribe({
      next: () => {
        this.loadChatGroups();
        this.loadChats(1, false);
        if (this.chatGroupExpanded[group.id]) {
          delete this.chatGroupChats[group.id];
          delete this.chatGroupExpanded[group.id];
        }
      },
      error: (error) => {
        console.error('Error al eliminar la carpeta:', error);
      },
    });
  }

  toggleSelectionMode(): void {
    this.selectionMode = !this.selectionMode;
    if (!this.selectionMode) {
      this.selectedChatIds.clear();
    }
  }

  isChatSelected(item: SidebarChatItem): boolean {
    if (!item.id) {
      return false;
    }
    return this.selectedChatIds.has(item.id);
  }

  isChatMoving(item: SidebarChatItem): boolean {
    return this.movingChatIds.has(item.chat_id);
  }

  toggleChatSelection(item: SidebarChatItem, event: Event): void {
    event.stopPropagation();
    if (!item.id) {
      return;
    }
    if (this.selectedChatIds.has(item.id)) {
      this.selectedChatIds.delete(item.id);
    } else {
      this.selectedChatIds.add(item.id);
    }
  }

  openMoveChatModalForChat(item: SidebarChatItem, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.closeAllDropdowns();
    if (!item.id) {
      return;
    }
    this.moveChatIds = [item.id];
    this.moveChatUuids = [item.chat_id];
    this.moveChatTargetGroupId = item.chat_group_id ?? null;
    this.showMoveChatModal = true;
  }

  openBulkMoveModal(): void {
    if (this.selectedChatIds.size === 0) {
      return;
    }
    this.closeAllDropdowns();
    this.moveChatIds = Array.from(this.selectedChatIds);
    this.moveChatUuids = this.collectChatUuidsByIds(this.moveChatIds);
    this.moveChatTargetGroupId = null;
    this.showMoveChatModal = true;
  }

  closeMoveChatModal(): void {
    this.showMoveChatModal = false;
    this.moveChatIds = [];
    this.moveChatUuids = [];
    this.moveChatTargetGroupId = null;
  }

  confirmMoveChat(): void {
    if (this.moveChatIds.length === 0) {
      this.closeMoveChatModal();
      return;
    }
    const targetGroupId = this.moveChatTargetGroupId ?? null;
    const movingIds = [...this.moveChatUuids];
    const request =
      this.moveChatIds.length === 1
        ? this.chatGroupsService.assignChat(this.moveChatIds[0], targetGroupId)
        : this.chatGroupsService.assignMultipleChats(
            this.moveChatIds,
            targetGroupId
          );

    this.markChatsAsMoving(movingIds);
    this.showMoveToast(targetGroupId, movingIds.length || this.moveChatIds.length);
    request.subscribe({
      next: () => {
        this.selectedChatIds.clear();
        this.selectionMode = false;
        this.clearMovingChats(movingIds);
        this.closeMoveChatModal();
        this.refreshSidebarData();
      },
      error: (error) => {
        console.error('Error al mover chats:', error);
        this.clearMovingChats(movingIds);
        this.closeMoveChatModal();
      },
    });
  }

  removeChatFromGroup(item: SidebarChatItem, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.closeAllDropdowns();
    if (!item.id) {
      return;
    }
    this.markChatsAsMoving([item.chat_id]);
    this.showMoveToast(null, 1);
    this.chatGroupsService.assignChat(item.id, null).subscribe({
      next: () => {
        this.clearMovingChats([item.chat_id]);
        this.refreshSidebarData();
      },
      error: (error) => {
        console.error('Error al sacar el chat de la carpeta:', error);
        this.clearMovingChats([item.chat_id]);
      },
    });
  }

  startNewChatInGroup(group: SidebarChatGroup, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const defaultAgent = this.agentState.findDefaultAgent();
    if (!defaultAgent) {
      this.router.navigate(['/dashboard/agents']);
      if (this.isMobile && this.isOpen) {
        this.sidebarService.toggleSidebar();
      }
      return;
    }

    this.selectAgent(defaultAgent);
    this.setPendingChatGroupId(group.id);

    const agentId = defaultAgent.agent_id || defaultAgent.agent_config?.id || Number(localStorage.getItem('agent_id'));
    if (!agentId) {
      return;
    }

    this.chatService.initializeChat(Number(agentId), group.id).subscribe({
      next: (data) => {
        const chatId = this.resolveInitializedChatId(data);
        if (chatId) {
          this.setChatGroupForChatId(chatId, group.id);
          localStorage.setItem('pending_initialized_chat_id', chatId);
        }
      },
      error: (error) => {
        console.error('Error al crear chat en carpeta:', error);
      },
    });
  }

  onChatDragStart(item: SidebarChatItem, groupId: number | null): void {
    this.draggingChat = item;
    this.draggingChatGroupId = groupId;
  }

  onChatDragEnd(): void {
    this.draggingChat = null;
    this.draggingChatGroupId = null;
    this.dragOverGroupId = null;
  }

  onGroupDragStart(group: SidebarChatGroup, event: Event): void {
    event.stopPropagation();
    this.draggingGroupId = group.id;
  }

  onGroupDragEnd(): void {
    this.draggingGroupId = null;
    this.dragOverGroupId = null;
  }

  onGroupDragOver(event: DragEvent, groupId: number): void {
    if (!this.draggingChat && !this.draggingGroupId) {
      return;
    }
    event.preventDefault();
    this.dragOverGroupId = groupId;
  }

  onGroupDrop(event: DragEvent, targetGroup: SidebarChatGroup): void {
    event.preventDefault();
    if (this.draggingChat && this.draggingChat.id) {
      const movedChat = this.draggingChat;
      const movedChatId = this.draggingChat.chat_id;
      const fromGroupId = this.draggingChatGroupId;
      this.markChatsAsMoving([movedChatId]);
      this.showMoveToast(targetGroup.id, 1);
      this.chatGroupsService
        .assignChat(movedChat.id!, targetGroup.id)
        .subscribe({
          next: () => {
            this.clearMovingChats([movedChatId]);
            this.moveChatLocally(
              movedChat as SidebarChatItem,
              targetGroup.id,
              fromGroupId
            );
          },
          error: (error) => {
            console.error('Error al mover chat:', error);
            this.clearMovingChats([movedChatId]);
          },
        });
    } else if (
      this.draggingGroupId &&
      this.draggingGroupId !== targetGroup.id
    ) {
      const fromIndex = this.chatGroupsList.findIndex(
        (group) => group.id === this.draggingGroupId
      );
      const toIndex = this.chatGroupsList.findIndex(
        (group) => group.id === targetGroup.id
      );
      if (fromIndex !== -1 && toIndex !== -1) {
        const [moved] = this.chatGroupsList.splice(fromIndex, 1);
        this.chatGroupsList.splice(toIndex, 0, moved);
        const payload = this.chatGroupsList.map((group, index) => ({
          id: group.id,
          order: index,
        }));
        this.chatGroupsService.reorderGroups(payload).subscribe({
          next: () => {
            this.chatGroupsList = this.chatGroupsList.map((group, index) => ({
              ...group,
              order: index,
            }));
          },
          error: (error) => {
            console.error('Error al reordenar carpetas:', error);
          },
        });
      }
    }
    this.draggingChat = null;
    this.draggingChatGroupId = null;
    this.dragOverGroupId = null;
    this.draggingGroupId = null;
  }

  onUngroupedDrop(event: DragEvent): void {
    event.preventDefault();
    if (this.draggingChat && this.draggingChat.id) {
      const movedChat = this.draggingChat;
      const movedChatId = this.draggingChat.chat_id;
      const fromGroupId = this.draggingChatGroupId;
      this.markChatsAsMoving([movedChatId]);
      this.showMoveToast(null, 1);
      this.chatGroupsService.assignChat(movedChat.id!, null).subscribe({
        next: () => {
          this.clearMovingChats([movedChatId]);
          this.moveChatLocally(
            movedChat as SidebarChatItem,
            null,
            fromGroupId
          );
        },
        error: (error) => {
          console.error('Error al sacar chat de carpeta:', error);
          this.clearMovingChats([movedChatId]);
        },
      });
    }
    this.draggingChat = null;
    this.draggingChatGroupId = null;
    this.dragOverGroupId = null;
  }

  toggleDeleteConfirmation(chatId: string, event: Event) {
    event.stopPropagation();
    this.closeAllDropdowns();
    this.chatToDelete = chatId;
  }

  closeAllChatOptions() {
    Object.keys(this.chatOptionsOpen).forEach((chatId) => {
      this.chatOptionsOpen[chatId] = false;
    });
  }

  closeAllDeleteConfirmations() {
    Object.keys(this.deleteConfirmationOpen).forEach((chatId) => {
      this.deleteConfirmationOpen[chatId] = false;
    });
    this.chatToDelete = null;
  }

  closeAllArchiveConfirmations() {
    Object.keys(this.archiveConfirmationOpen).forEach((chatId) => {
      this.archiveConfirmationOpen[chatId] = false;
    });
    this.chatToArchive = null;
  }

  confirmDeleteChat() {
    const chatId = this.chatToDelete;
    if (!chatId) return;
    this.chatToDelete = null;

    this.chatService.deleteChat(chatId).subscribe({
      next: () => {
        this.removeChatFromListsByUuid(chatId);
        this.sortAllChatLists();
        this.loadChatGroups();

        if (this.chatState.getPersistedChatId() === chatId) {
          this.chatState.setPersistedChatId(null);
          this.chatState.resetChat();
          this.router.navigate(['/dashboard/chat']);
        }

        this.showToast('success', 'Éxito', 'Chat eliminado correctamente');
      },
      error: (error) => {
        console.error('Error al eliminar el chat:', error);
        
        this.showToast('alert', 'Error', 'No se pudo eliminar el chat');
      }
    });
  }

  archiveChat(chatId: string, event: Event) {
    event.stopPropagation();
    this.closeAllDropdowns();
    this.chatToArchive = chatId;
  }

  confirmArchiveChat() {
    const chatId = this.chatToArchive;
    if (!chatId) return;
    this.chatToArchive = null;

    this.chatService.archiveChat(chatId).subscribe({
      next: () => {
        this.removeChatFromListsByUuid(chatId);
        this.sortAllChatLists();
        this.loadChatGroups();

        if (this.chatState.getPersistedChatId() === chatId) {
          this.chatState.setPersistedChatId(null);
          this.chatState.resetChat();
          this.router.navigate(['/dashboard/chat']);
        }

        this.showToast('success', 'Éxito', 'Chat archivado correctamente');
      },
      error: (error) => {
        console.error('Error al archivar el chat:', error);
        
        this.showToast('alert', 'Error', 'No se pudo archivar el chat');
      }
    });
  }

  toggleArchiveConfirmation(chatId: string, event: Event) {
    event.stopPropagation();
    const isOpening = !this.archiveConfirmationOpen[chatId];
    if (isOpening) {
      this.closeAllDropdowns();
    }
    this.archiveConfirmationOpen[chatId] = isOpening;
    this.chatOptionsOpen[chatId] = false;
  }

  private showToast(
    type: 'info' | 'success' | 'warning' | 'alert',
    title: string,
    message: string,
    duration: number = 3000,
    keepBackground: boolean = false
  ): void {
    if (!keepBackground) {
      this.lastBackgroundNotification = null;
    }
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.toastType = type;
    this.toastTitle = title;
    this.toastMessage = message;
    this.show = false;

    requestAnimationFrame(() => {
      this.show = true;

      this.toastTimer = setTimeout(() => {
        this.show = false;
        this.toastTimer = null;
      }, duration);
    });
  }

  private applyActiveChat(activeChatId: string | null): void {
    if (!activeChatId) {
      return;
    }
    const resolvedTarget = this.chatState.resolveChatId(activeChatId);
    const apply = (item: SidebarChatItem) => {
      const resolvedItem = this.chatState.resolveChatId(item.chat_id);
      item.active =
        item.chat_id === activeChatId || resolvedItem === resolvedTarget;
    };
    this.ungroupedChats.forEach(apply);
    Object.keys(this.chatGroupChats).forEach((groupId) => {
      this.chatGroupChats[Number(groupId)]?.forEach(apply);
    });
  }

  handleToastClick(): void {
    if (!this.lastBackgroundNotification?.chatId) {
      return;
    }
    this.openChatById(this.lastBackgroundNotification.chatId);
    this.lastBackgroundNotification = null;
    this.show = false;
  }

  private bumpChatSorting(chatId: string): void {
    if (!chatId) {
      return;
    }
    const now = new Date();
    const resolvedTarget = this.chatState.resolveChatId(chatId);
    const matches = (item: SidebarChatItem) => {
      if (item.chat_id === chatId) {
        return true;
      }
      const resolvedItem = this.chatState.resolveChatId(item.chat_id);
      return resolvedItem === resolvedTarget;
    };

    let updated = false;
    this.ungroupedChats.forEach((item) => {
      if (matches(item)) {
        item.sort_at = now;
        item.isProcessing = false;
        updated = true;
      }
    });
    Object.keys(this.chatGroupChats).forEach((groupId) => {
      this.chatGroupChats[Number(groupId)]?.forEach((item) => {
        if (matches(item)) {
          item.sort_at = now;
          item.isProcessing = false;
          updated = true;
        }
      });
    });

    if (updated) {
      // No reordenar aquí para evitar “saltos” cuando luego llega el refresh.
      this.cdr.markForCheck();
    }
  }

  private openChatById(chatId: string): void {
    const item = this.findChatItemByUuid(chatId);
    if (item) {
      this.selectItem(item);
      return;
    }
    localStorage.setItem('chat_id', chatId);
    this.chatState.setChatId(chatId, true);
    this.router.navigate(['/dashboard/chat']);
  }

  private clearActiveChats(): void {
    this.ungroupedChats.forEach((i) => (i.active = false));
    Object.keys(this.chatGroupChats).forEach((groupId) => {
      this.chatGroupChats[Number(groupId)]?.forEach((item) => {
        item.active = false;
      });
    });
  }

  private findChatItemByUuid(chatId: string): SidebarChatItem | null {
    const inUngrouped = this.ungroupedChats.find((i) => i.chat_id === chatId);
    if (inUngrouped) {
      return inUngrouped;
    }
    for (const groupId of Object.keys(this.chatGroupChats)) {
      const found = this.chatGroupChats[Number(groupId)]?.find(
        (item) => item.chat_id === chatId
      );
      if (found) {
        return found;
      }
    }
    return null;
  }

  private findChatByUuid(chatId: string): boolean {
    return !!this.findChatItemByUuid(chatId);
  }

  private ensureGroupChatList(groupId: number): void {
    if (!this.chatGroupChats[groupId]) {
      this.chatGroupChats[groupId] = [];
    }
  }

  private setPendingChatGroupId(groupId: number | null): void {
    if (groupId === null || groupId === undefined) {
      localStorage.removeItem('pending_chat_group_id');
      return;
    }
    localStorage.setItem('pending_chat_group_id', groupId.toString());
  }

  private resolveInitializedChatId(response: any): string | null {
    const direct = response?.chat_uuid || response?.chat_id;
    if (direct) {
      return direct;
    }
    return (
      response?.data?.chat_uuid ||
      response?.data?.chat_id ||
      response?.data?.chat?.chat_id ||
      null
    );
  }

  private setChatGroupForChatId(chatId: string, groupId: number): void {
    if (!chatId || groupId === null || groupId === undefined) {
      return;
    }
    localStorage.setItem(`pending_chat_group_for_${chatId}`, groupId.toString());
  }

  private consumeChatGroupForChatId(chatId: string | null): number | null {
    if (!chatId) {
      return null;
    }
    const key = `pending_chat_group_for_${chatId}`;
    const value = localStorage.getItem(key);
    if (!value) {
      return null;
    }
    localStorage.removeItem(key);
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private consumePendingChatGroupId(): number | null {
    const value = localStorage.getItem('pending_chat_group_id');
    if (!value) {
      return null;
    }
    localStorage.removeItem('pending_chat_group_id');
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private removeChatFromListsByUuid(chatId: string): void {
    this.ungroupedChats = this.ungroupedChats.filter(
      (item) => item.chat_id !== chatId
    );
    Object.keys(this.chatGroupChats).forEach((groupId) => {
      this.chatGroupChats[Number(groupId)] = (
        this.chatGroupChats[Number(groupId)] || []
      ).filter((item) => item.chat_id !== chatId);
    });
  }

  private normalizeGroupIcon(icon?: string | null): string | null {
    if (!icon) {
      return this.defaultGroupIcon;
    }
    const trimmed = icon.trim();
    return trimmed ? trimmed : this.defaultGroupIcon;
  }

  private collectChatUuidsByIds(ids: number[]): string[] {
    if (!ids || ids.length === 0) {
      return [];
    }
    const idSet = new Set(ids);
    const found: string[] = [];
    this.ungroupedChats.forEach((item) => {
      if (item.id && idSet.has(item.id)) {
        found.push(item.chat_id);
      }
    });
    Object.values(this.chatGroupChats).forEach((items) => {
      items.forEach((item) => {
        if (item.id && idSet.has(item.id)) {
          found.push(item.chat_id);
        }
      });
    });
    return found;
  }

  private markChatsAsMoving(chatIds: string[]): void {
    chatIds.forEach((id) => {
      if (id) {
        this.movingChatIds.add(id);
      }
    });
  }

  private clearMovingChats(chatIds: string[]): void {
    chatIds.forEach((id) => {
      if (id) {
        this.movingChatIds.delete(id);
      }
    });
  }

  private showMoveToast(targetGroupId: number | null, count: number): void {
    const label = this.getGroupLabel(targetGroupId);
    const title = count > 1 ? `Moviendo ${count} chats` : 'Moviendo chat';
    const message = `Moviendo ${count > 1 ? 'chats' : 'chat'} a ${label}`;
    this.showToast('info', title, message, 2500, true);
  }

  private getGroupLabel(targetGroupId: number | null): string {
    if (targetGroupId === null || targetGroupId === undefined) {
      return 'Sin carpeta';
    }
    const group = this.chatGroupsList.find((g) => g.id === targetGroupId);
    return group?.name || 'carpeta';
  }

  @HostListener('document:click')
  handleDocumentClick() {
    this.closeAllDropdowns();
  }

  toggleDropdownAgent(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const wasOpen = this.isDropdownAgentOpen;
    this.closeAllDropdowns();
    this.isDropdownAgentOpen = !wasOpen;
  }
  openSettings() {
    if (this.isDropdownAgentOpen === true) {
      this.isDropdownAgentOpen = false;
    }
    this.router.navigate(['/dashboard/settings', 'general']);

    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  openProfile() {
    if (this.isDropdownAgentOpen === true) {
      this.isDropdownAgentOpen = false;
    }
    this.navbarViewManagerService.closeAllNavbarViewsExcept('userProfile');
    this.profileService.showUserProfile();
    this.isProfileMenuOpen = false;
    this.router.navigate(['/dashboard/profile']);
    
    if (this.isMobile) {
      const sidebarProfileOpen = this.sidebarService.isOpenValue;
      if (sidebarProfileOpen) {
        this.sidebarService.toggleSidebar();
      }
    }
  }

  openSearch() {
    this.searchChatViewService.toggleSearchChatModal();

    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }


  selectAgent(agent: any): void {
    const idToUse = (typeof agent.agent_id === 'number' && !isNaN(agent.agent_id))
      ? agent.agent_id
      : (typeof agent.agent_config.id === 'number' && !isNaN(agent.agent_config.id))
        ? agent.agent_config.id
        : null;

    if (idToUse) {
      const agentType = agent.category === 'datasources' ? 'datasource' : 'agent';
      
      this.currentAgentData = agent;
      
      this.applyAgentConfiguration(agent, idToUse);
      
      this.cdr.detectChanges();
      
      this.financialDocumentsViewService.hideFinancialDocuments();

      localStorage.setItem('type_agent', agentType);
      localStorage.setItem('agent_id', idToUse.toString());

      if (idToUse === 3) {
        this.financialDocumentsViewService.showFinancialDocuments();
      }

      this.agentState.setAgentId(idToUse);
      this.agentState.setCurrentAgent(agent.agent_config.name);
      
      this.chatState.startNewChat(this.agentState, this.navbarViewManagerService);
      this.isDropdownAgentOpen = false;
      this.navbarViewManagerService.closeAllNavbarViews();

      this.router.navigate(['/dashboard/chat']);

      if (this.isMobile && this.isOpen) {
        this.sidebarService.toggleSidebar();
      }
    } else {
      console.error('No se puede abrir la conversación para este agente');
    }
  }

  newChat() {
    this.setPendingChatGroupId(null);
    const defaultAgent = this.agentState.findDefaultAgent();

    if (defaultAgent) {
      this.selectAgent(defaultAgent);
    } else {
      // No hay agentes activos: navegar a la pantalla de agentes
      this.router.navigate(['/dashboard/agents']);

      if (this.isMobile && this.isOpen) {
        this.sidebarService.toggleSidebar();
      }
    }
  }

  private initializeAgent(): void {
    const currentAgentId = localStorage.getItem('agent_id');

    if (!currentAgentId) {
      this.setFirstAvailableAgent();
    } else {
      const agentExists = this.agentExists(Number(currentAgentId));
      if (agentExists) {
        this.setAgent(Number(currentAgentId));
      } else {
        this.setFirstAvailableAgent();
      }
    }
  }

  private setFirstAvailableAgent(): void {
    if (this.products.length > 0) {

      const miAgenteAgent = this.products.find(agent => 
        agent.agent_config?.name?.toLowerCase() === 'mi agente'
      );
      
      const selectedAgent = miAgenteAgent || this.products[0];
      
      let agentId: number;
      let typeAgent: string;

      if (selectedAgent.datasource_id) {
        agentId = selectedAgent.datasource_id;
        typeAgent = 'datasource';
      } else if (selectedAgent.agent_id) {
        agentId = selectedAgent.agent_id;
        typeAgent = 'agent';
      } else if (selectedAgent.agent_config?.id) {
        agentId = selectedAgent.agent_config.id;
        typeAgent = 'agent';
      } else {
        agentId = selectedAgent.id;
        typeAgent = 'agent';
      }

      localStorage.setItem('type_agent', typeAgent);
      this.setAgent(agentId);
    }
  }

  private agentExists(agentId: number): boolean {
    const typeAgent = localStorage.getItem('type_agent');
    const isDatasource = typeAgent === 'datasource';

    return this.products.some((agent: AgentData) => {
      if (isDatasource) {
        return agent.datasource_id === agentId;
      } else {
        return (
          agent.agent_id === agentId || 
          agent.agent_config?.id === agentId ||
          agent.id === agentId
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    if (typeof document !== 'undefined') {
      document.body.classList.remove('dashboard-menu-collapsed');
    }
  }

  private sortAgents(agents: AgentData[]): AgentData[] {
    const miAgenteIndex = agents.findIndex(agent => 
      agent.agent_config?.name?.toLowerCase() === 'mi agente'
    );
    
    if (miAgenteIndex !== -1) {
      const miAgente = agents[miAgenteIndex];
      const otherAgents = agents.filter((_, index) => index !== miAgenteIndex);
      return [miAgente, ...otherAgents];
    }
    
    return agents;
  }

  filterAgents(): void {
    let filtered: AgentData[];
    
    if (!this.agentSearchQuery.trim()) {
      filtered = [...this.products];
    } else {
      const query = this.agentSearchQuery.toLowerCase().trim();
      filtered = this.products.filter(product =>
        product.agent_config?.name?.toLowerCase().includes(query)
      );
    }
    
    this.filteredProducts = this.sortAgents(filtered);
  }

  onAgentSearchChange(): void {
    this.filterAgents();
  }

  goToAllAgents(): void {
    this.navbarViewManagerService.closeAllNavbarViews();
    this.router.navigate(['/dashboard/agents']);

    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }
}
